/**
 * Typed wrappers around the Tauri commands.
 *
 * The host UI runs inside the Tauri window, but the same bundle is served to
 * viewers in a plain browser, so every call here must be safe to *reach* in a
 * browser and fail with a readable message instead of a stack trace. `isTauri()`
 * is what routes decide between the real picker and a "open the desktop app"
 * notice.
 */

import { Channel, invoke } from '@tauri-apps/api/core'
import { emit, listen } from '@tauri-apps/api/event'
import type { Social } from './social'
import { isDesktopApp } from './platform'

/** True only inside the Tauri window (WebView2 injects `__TAURI_INTERNALS__`). */
export function isTauri(): boolean {
  return typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window
}

async function call<T>(command: string, args?: Record<string, unknown>): Promise<T> {
  if (!isDesktopApp) {
    throw new Error(`"${command}" only works in the Sofa desktop app.`)
  }
  return invoke<T>(command, args)
}

/** A shareable window, mirroring `sshare_capture::WindowInfo`. */
export interface WindowInfo {
  hwnd: number
  pid: number
  title: string
  exe: string
  width: number
  height: number
  is_minimized: boolean
}

export interface MonitorInfo {
  index: number
  name: string
  width: number
  height: number
}
export function listMonitors(): Promise<MonitorInfo[]> {
  return call('list_monitors')
}

/** A process with an audio session, mirroring `sshare_audio::AudioProcessInfo`. */
export interface AudioProcessInfo {
  pid: number
  exe: string
  display_name: string
  is_active: boolean
}

/** Build metadata, mirroring `sshare_desktop_lib::AppInfo`. */
export interface AppInfo {
  name: string
  version: string
  protocol_version: number
  min_bitrate_bps: number
  max_bitrate_bps: number
  max_viewer_cap: number
}

/** Arguments for `start_stream`, mirroring `StartStreamRequest`. */
export interface StartStreamRequest {
  hwnd: number
  monitor_index: number | null
  audio_pid: number
  audio_follow_window?: boolean
  include_process_tree: boolean
  exclude_process_tree: boolean
  /** Keep excluding this app (exe name fragment) even if it starts later. */
  exclude_exe?: string
  bitrate_bps: number
  fps: number
  viewer_cap: number
  /** Empty string means no password. */
  password: string
  display_name: string
  share: ShareMode
  access_token?: string
  encoder: EncoderChoice
}

/** The video encoder setting. `auto` picks AMD AMF where the GPU has it. */
export type EncoderChoice = 'auto' | 'amf' | 'mf'

/**
 * Where viewers connect. `tunnel` and `lan` run the signaling server on this
 * PC; `tunnel` publishes it through a Cloudflare quick tunnel (no hosted
 * server, no open ports), `lan` only on the local network.
 */
export type ShareMode = 'tunnel' | 'lan' | 'server'

export interface ModeAvailability {
  available: boolean
  reason: string | null
}

export type SharingAvailability = Record<ShareMode, ModeAvailability>
export const sharingAvailability = () => call<SharingAvailability>('sharing_availability')

/** What `start_stream` reports back. */
export interface StreamSummary {
  room_code: string
  /** The link to send viewers. */
  share_url: string
  /** Actual route, which may differ from the requested server mode. */
  share?: ShareMode
  width: number
  height: number
  bitrate_bps: number
  fps: number
  viewer_cap: number
}

/** Native session state, retained while the webview reloads. */
export interface StreamSnapshot {
  revision: number
  summary: StreamSummary
  hwnd: number
  monitor_index: number | null
  elapsed_ms: number
  events: HostEvent[]
}

export const activeStream = () => call<StreamSnapshot | null>('active_stream')

/** One second of the host's own view, mirroring `sshare_host::HostStats`. */
export interface HostStats {
  fps: number
  video_kbps: number
  worst_encode_ms: number
  /** The rate the encoder is set to now. */
  target_kbps: number
  /** The slowest viewer path's congestion-control estimate, if known. */
  estimate_kbps: number | null
  /** Encoding stepped down to half rate because it could not keep up. */
  frame_rate_reduced: boolean
  viewers: {
    peer_id: string
    state: string
    packets_sent: number
    frames_dropped: number
    estimate_kbps: number | null
  }[]
}

/** What the running session reports, mirroring `sshare_host::HostEvent`. */
export type HostEvent =
  | { type: 'viewer_joined'; peer_id: string; name: string }
  | { type: 'viewer_left'; peer_id: string }
  | { type: 'viewer_connected'; peer_id: string }
  | { type: 'viewer_disconnected'; peer_id: string }
  | { type: 'chat'; from: string; name: string; text: string; ts_ms: number }
  | ({ type: 'stats' } & HostStats)
  | { type: 'warning'; message: string }
  | { type: 'stopped'; reason: string | null }

/** Subscribe to the session's events. Resolves to the unsubscribe function. */
export async function onHostEvent(
  handler: (event: HostEvent, revision: number) => void,
): Promise<() => void> {
  if (!isTauri()) return () => {}
  return listen<HostEvent & { revision: number }>('host_event', (event) =>
    handler(event.payload, event.payload.revision),
  )
}

export type StreamRecovery =
  | { state: 'recovering' }
  | { state: 'recovered'; summary: StreamSummary; message: string }
  | { state: 'failed'; message: string }

export async function onStreamRecovery(
  handler: (event: StreamRecovery) => void,
): Promise<() => void> {
  if (!isTauri()) return () => {}
  return listen<StreamRecovery>('stream_recovery', (event) => handler(event.payload))
}

/** What the tray mirrors about the stream. */
export interface LiveState {
  live: boolean
  /** The status line, already worded in the user's language. */
  status: string
  share_url: string | null
}

export interface TrayLabels {
  copy: string
  mini: string
  open: string
  stop: string
  quit: string
}

/** A snapshot of the host screen for the mini view. */
export interface MiniState {
  live: boolean
  elapsed: string
  health: 'great' | 'busy' | 'slow'
  fps: number | null
  /** Video bitrate being sent, in Mbps. */
  mbps: number | null
  encodeMs: number | null
  viewers: { id: string; name: string; connected: boolean; bars: 1 | 2 | 3 | 4; dropped: number }[]
  chatCount: number
}

export type MiniAction = 'stop' | 'expand' | 'chat'
export type TrayAction = 'stop'

export const encoderInfo = () => call<{ amf_available: boolean }>('encoder_info')
export const setLiveState = (live: LiveState) => call<void>('set_live_state', { live })
export const setTrayLabels = (labels: TrayLabels) => call<void>('set_tray_labels', { labels })
export const showMini = () => call<void>('show_mini')
export const hideMini = () => call<void>('hide_mini')
export const toggleMini = () => call<void>('toggle_mini_view')
export const openMain = () => call<void>('open_main')

/** One chat line for the in-game overlay. */
export interface OverlayLine {
  name: string
  item: Social
}
export type OverlaySide = 'left' | 'right'
export const showOverlay = (side: OverlaySide, hotkey: string) =>
  call<void>('show_overlay', { side, hotkey })
export const setOverlayHotkey = (hotkey: string) => call<void>('set_overlay_hotkey', { hotkey })
export const setOverlaySide = (side: OverlaySide) => call<void>('set_overlay_side', { side })
export const hideOverlay = () => call<void>('hide_overlay')

/** Get the pointer, ping and ink overlay ready for a stream, and release it after. */
export const startMarks = () => call<void>('start_marks')
export const stopMarks = () => call<void>('stop_marks')
export const endOverlayInput = () => call<void>('end_overlay_input')
export const publishOverlayLine = (line: OverlayLine) =>
  isTauri() ? emit('overlay_line', line) : Promise.resolve()
export const sendOverlayMessage = (text: string) =>
  isTauri() ? emit('overlay_send', text) : Promise.resolve()

export const publishMiniState = (state: MiniState) =>
  isTauri() ? emit('mini_state', state) : Promise.resolve()
export const requestMiniState = () => (isTauri() ? emit('mini_ready') : Promise.resolve())
export const sendMiniAction = (action: MiniAction) =>
  isTauri() ? emit('mini_action', action) : Promise.resolve()

async function subscribe<T>(name: string, handler: (payload: T) => void): Promise<() => void> {
  if (!isTauri()) return () => {}
  return listen<T>(name, (event) => handler(event.payload))
}
export const onMiniState = (handler: (state: MiniState) => void) => subscribe('mini_state', handler)
export const onMiniReady = (handler: () => void) => subscribe('mini_ready', handler)
export const onMiniAction = (handler: (action: MiniAction) => void) =>
  subscribe('mini_action', handler)
export const onOverlayLine = (handler: (line: OverlayLine) => void) =>
  subscribe('overlay_line', handler)
export const onOverlaySide = (handler: (side: OverlaySide) => void) =>
  subscribe('overlay_side', handler)
export const onOverlayInput = (handler: (open: boolean) => void) =>
  subscribe('overlay_input', handler)
/** A pointer, ping or stroke for the host's mark overlay. */
export interface MarkEvent {
  from: string
  kind: 'ping' | 'pointer' | 'ink'
  stroke: number
  points: [number, number][]
}
export const onMarkEvent = (handler: (mark: MarkEvent) => void) => subscribe('mark_event', handler)
export const onOverlaySend = (handler: (text: string) => void) => subscribe('overlay_send', handler)
export const onTrayAction = (handler: (action: TrayAction) => void) =>
  subscribe('tray_action', handler)

export const appInfo = () => call<AppInfo>('app_info')

/** A newer release, mirroring `sshare_desktop_lib::UpdateInfo`. */
export interface UpdateInfo {
  version: string
  notes: string | null
}

export const checkForUpdate = () => call<UpdateInfo | null>('check_for_update')
export const installUpdate = () => call<void>('install_update')

export const listWindows = () => call<WindowInfo[]>('list_windows')

/** Turn the OS window-event hooks on or off; they only matter while the picker shows. */
export const watchWindows = (enabled: boolean) => call<void>('watch_windows', { enabled })

/** Top-level windows were opened, closed, renamed or minimised. Debounced, no payload. */
export const onWindowsChanged = (handler: () => void) => subscribe('windows_changed', handler)

export const listAudioProcesses = () => call<AudioProcessInfo[]>('list_audio_processes')

/** A small JPEG of one window, as an object URL the caller must revoke. */
export async function windowThumbnail(hwnd: number): Promise<string> {
  const bytes = await call<ArrayBuffer>('window_thumbnail', { hwnd })
  return URL.createObjectURL(new Blob([bytes], { type: 'image/jpeg' }))
}

/**
 * Stream live-preview JPEGs of a window. `onFrame` gets an object URL for each
 * (the previous one is revoked here). Resolves to a stop function; call it when
 * the page is left or hidden, since the capture only runs while it is wanted.
 */
export async function startPreview(
  target: { hwnd: number | null; monitorIndex: number | null },
  onFrame: (url: string) => void,
): Promise<() => Promise<void>> {
  let current: string | null = null
  let active = true
  const frames = new Channel<ArrayBuffer>()
  frames.onmessage = (bytes) => {
    if (!active) return
    const next = URL.createObjectURL(new Blob([bytes], { type: 'image/jpeg' }))
    onFrame(next)
    if (current) URL.revokeObjectURL(current)
    current = next
  }
  await call<void>('start_preview', {
    hwnd: target.hwnd,
    monitorIndex: target.monitorIndex,
    frames,
  })
  return async () => {
    active = false
    if (current) URL.revokeObjectURL(current)
    current = null
    await call<void>('stop_preview').catch(() => {})
  }
}

/** Native startup milestones; no percentage or duration is implied. */
export type StartupStage = 'preparing_link' | 'opening_room' | 'starting_video'

export async function startStream(
  request: StartStreamRequest,
  onProgress: (stage: StartupStage) => void = () => {},
): Promise<StreamSummary> {
  const progress = new Channel<StartupStage>()
  let active = true
  progress.onmessage = (stage) => {
    if (active) onProgress(stage)
  }
  try {
    return await call<StreamSummary>('start_stream', { request, progress })
  } finally {
    active = false
  }
}

export const stopStream = () => call<void>('stop_stream')

export const setBitrate = (bitrateBps: number) =>
  call<number>('set_bitrate', { bitrate_bps: bitrateBps })

/** Move the running stream to another window (`hwnd`) or monitor; viewers stay connected. */
export const switchSource = (hwnd: number, monitorIndex: number | null = null) =>
  call<void>('switch_source', { hwnd, monitor_index: monitorIndex })

export const sendChat = (text: string) => call<void>('send_chat', { text })

/** Turn an unknown thrown value into something safe to render. */
export function errorMessage(error: unknown): string {
  if (typeof error === 'string') return error
  if (error instanceof Error) return error.message
  return String(error)
}
